/**
 * @file The restore half of form point-in-time recovery (SYD-2450 phase 2):
 * calls `restoreFormVersionTo` for the open form version AND every table of the
 * selected moment, mutates `formRecoveryStore` at this call site once the write
 * lands, and records one `formUndoStack` entry covering the version's fields.
 * Consumers: `FormRecoveryConfirmModalContent`, which calls `restore`, and
 * `FormPageContent`, which mounts this hook purely for its registration effect
 * so the entry outlives the confirmation modal and the sidebar drawer both
 * closing. `registerTarget` is refcounted, so mounting the hook in more than
 * one place at once is safe — see `formUndoStack.ts`.
 *
 * One call restores everything, because the callable commits the version document
 * and every named table's rows in one transaction. The moment names its own tables
 * and each one's `tableReads` entry carries the digests `readArTableAt` returned
 * for THAT cursor, so the request is built by walking the moment and picking up the
 * entry stamped with the cursor being restored — never a digest read at another
 * moment.
 *
 * **A table that could not be read refuses the whole restore.** It has no digests,
 * so it cannot be named in the request, and a restore that quietly left it out
 * would put the form at the retained moment and that table at the present — the
 * partial restore the confirmation exists to prevent, and one no single undo
 * reverses. The refusal is transient: the operator selects the moment again and the
 * table is read again. `FormRecoveryConfirmModalContent` disables Confirm and says
 * the same thing in its own words; the throw here is the backstop for a table whose
 * read was dropped between the render and the press.
 *
 * **The report covers rows; the undo does not.** `restoredFrom.count` is built from
 * `response.restored`, the version document's diff, and a restore that changed only
 * rows writes no version document at all — so that count is `0` for a restore that
 * rewrote a hundred rows, and reading the banner off it alone would say nothing
 * happened. `restoredFrom.tables` carries the other half: each table's written rows
 * counted by action, the rows it was told to leave behind, and the trash operation
 * its deletes were filed under.
 *
 * The undo entry is built from `response.restored` and only from it. Reverting it
 * puts the version's fields back and leaves every restored row where the restore put
 * it. Row-level undo already exists as its own mechanism — `arTableRowUndo`, driven
 * by the `trashOperationId` each `RestoredArTable` carries — and joining the two
 * stacks is separate work. The asymmetry is reachable and visible: a mixed restore
 * reports rows and offers a live undo, so the banner reads `tables`, words that
 * button as the form's values alone, and says in its own line that the rows stay.
 *
 * Registers its own `UndoTarget` under the plain document path
 * `forms/{formId}/formVersions/{formVersionId}`, distinct from `useFieldUI`'s
 * per-field keys, so one `formUndoStack.record()` call reverts every restored
 * field and reference together (the design note's `ArCell.tsx:467` precedent).
 *
 * Only a `restored` diff entry of `kind: 'field'`, present on BOTH sides,
 * becomes an undo change. Two exclusions, both restored on the document by the
 * callable but left out of the undo entry:
 *
 * - An add or a delete (either side `null`). `restoreFormVersionTo` deletes the
 *   whole `fields.{id}` key when the retained side is absent, and adds it whole
 *   when the live side is absent (backend design note, section 5) — reverting
 *   either correctly needs a delete or a rebuilt object at that PARENT path, not
 *   at `.value`, and the response carries no `meta` to rebuild an added key's
 *   object with.
 * - Every `kind: 'entityReference'` entry, always. A reference's identity is
 *   several keys (`entityRecordId`, `entityId`, `entityGroupId`) with no single
 *   `.value` to scope an undo path to, so a change can only be recorded at the
 *   parent `entityReferences.{key}` path against the whole stored object. The
 *   diff's `FormPitrValue.value` for a reference carries no `meta` — it is
 *   `referenceValue()`'s `{entityRecordId, entityId, entityGroupId,
 *   entityRecordName}` — while the callable's own write at that path is
 *   `{ ...retainedReference, meta: restampedMeta(...) }`, always a freshly
 *   restamped `meta`. Those two shapes can never be deep-equal, so
 *   `formUndoStack.ts`'s per-change guard (`runStep`, ~line 490) would find the
 *   live value never matches what this hook recorded as `next` and discard the
 *   *entire* entry as a conflict on the very first ⌘Z — including any field
 *   changes recorded alongside it. And were the guard ever to pass, `apply()`
 *   writing the parent path with `persist.update` would overwrite rather than
 *   merge, permanently dropping the reference's `meta`. Fields avoid this only
 *   because their undo path is scoped to `fields.{id}.value`, leaving `meta`
 *   alone; a reference has no such leaf.
 *
 * The version is captured from the snapshot at render, matching
 * `useFormRecoveryRead`: fixed before the first await, never read off the bare
 * proxy afterward. So are the moment, its tables and their digests. `restoring`,
 * `restoreError` and `restoredFrom` live on `formRecoveryStore` rather than in
 * local state, because the banner that draws canvas state G outlives both the
 * confirmation modal and the sidebar drawer.
 *
 * `stillWanted` is the same guard shape both sibling hooks carry, and it covers the
 * refusal as well as the success: a response landing after `formRecoveryStore.clear()`
 * — the user clicked a lineage row for another version while the callable was out —
 * would otherwise paint a restored banner, with a live Undo, over a version that was
 * never touched. Nothing is recorded there either. `formUndoStack` is cleared on the
 * same version switch, so the stack a late `record` reached would be the NEW version's:
 * `record` zeroes the redo stack before it pushes, so it would destroy a redo chain
 * built on a form this restore never touched and leave an entry whose target has no
 * adapter, spending the first press that finds it.
 *
 * **The entry is recorded only once the write is visible to the guard that validates
 * it.** `runStep` keeps a change only while `adapter.read` still returns what was
 * recorded as `next`, and that read resolves off `formStore.state.formVersion.data` —
 * a mirror the Firestore listener fills, on a different connection from the callable
 * and with no latency compensation. The whole entry is one atomic merge, so in the gap
 * every change fails together, `runStep` reads total failure as a conflict, discards
 * the entry and empties the redo stack, and the banner hides itself with it: the
 * operator's only way back is a second full restore. So the record waits for
 * `modifiedAt` to reach `response.restoredAtMillis` — the stamp the transaction wrote —
 * and records anyway after `RESTORE_MIRROR_TIMEOUT_MS`, because an undo affordance that
 * may conflict beats one that silently never appears.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { subscribe, useSnapshot } from 'valtio';
import type { Timestamp } from 'firebase/firestore';
import get from 'lodash/get';

import type {
  ArTableId,
  ArTableRowDiffEntry,
  FormPitrDiffEntry,
  RestoreFormVersionRequest,
  RestoreFormVersionResponse,
} from '#types';
import { restoreFormVersionTo } from '@/services/formPitrFunctions';
import type { RecoveryMoment } from '@/store/form/formRecoveryStore';
import { readCallableError } from '@/workflows/runtime/readCallableError';

import { formRecoveryStore, formStore } from '../../store';
import { formUndoStack, type UndoChange, type UndoTarget } from '../../store/form';

/** One table of the request, as the wire contract spells it. */
type RestoreRequestTable = NonNullable<RestoreFormVersionRequest['tables']>[number];

/** What the operator chose in the confirmation, rather than what this hook assumed. */
export type FormRecoveryRestoreOptions = {
  /**
   * Proceed without the rows no table's restore can write. The confirmation owns
   * this choice — it is the one the `Restore without the locked rows` button states —
   * so it is threaded in rather than derived here from a read this hook does not own.
   */
  skipBlockedRows: boolean;
};

export type FormRecoveryRestore = {
  /** The page names a form version, so a restore has a target to write into. */
  canRestore: boolean;
  /**
   * Restores the form version and every table of the moment named by its opaque
   * `atCursorMicros`. `expectedLastModifiedMillis` must be the
   * `liveLastModifiedMillis` the operator's confirmation actually showed — the
   * compare-and-set the callable refuses against. Resolves once the write has
   * landed and the undo entry is recorded; rejects with the callable's own error
   * untouched, so a `failed-precondition`'s `code` and `details` reach the caller
   * for `RestoreFormVersionConflict` to be read off it. Never retries.
   *
   * Rejects before sending anything when the moment is no longer loaded, or when a
   * table it names has no successful read at this cursor to take digests from.
   */
  restore: (
    atCursorMicros: number,
    expectedLastModifiedMillis: number | null,
    options: FormRecoveryRestoreOptions,
  ) => Promise<RestoreFormVersionResponse>;
};

const docPathFor = (formId: string, formVersionId: string): string =>
  `forms/${formId}/formVersions/${formVersionId}`;

/**
 * How long the undo entry waits for the restore's own write to reach the store's
 * mirror of the version. Five seconds is roughly ten times the worst ordinary sum:
 * `persistDocument` defers an incoming snapshot for as long as `writeQueue.isBlocked()`,
 * which a field typed just before Confirm holds for one `SAVE_DEBOUNCE_MS` (350ms) plus
 * that write's own round trip, and the restore's commit then has to be delivered on the
 * listener. Past that the wait is no longer compensating for latency, and an undo
 * affordance that may conflict is worth more than one the operator never gets.
 */
const RESTORE_MIRROR_TIMEOUT_MS = 5_000;

/**
 * The store's mirror of the version's `modifiedAt`, in millis, `0` when it carries none.
 * `modifiedAt` is maintained by the persistence layer rather than declared on
 * `FormVersion`, so it is read exactly as `persistDocument` reads it for its own
 * conflict resolution.
 */
const mirroredModifiedAtMillis = (): number =>
  (
    (formStore.state.formVersion.data as Record<string, unknown> | null)?.modifiedAt as
      | Timestamp
      | undefined
  )?.toMillis?.() ?? 0;

/**
 * Resolves once the mirror carries a write at least as new as the restore's own, or
 * after `RESTORE_MIRROR_TIMEOUT_MS`, whichever is first. Subscribing to the document
 * slot is the sanctioned use of a Valtio subscription: the thing being waited on is an
 * external system's delivery, and the check it runs is idempotent.
 */
const restoreLanded = (restoredAtMillis: number): Promise<void> =>
  new Promise<void>((resolve) => {
    if (mirroredModifiedAtMillis() >= restoredAtMillis) {
      resolve();
      return;
    }
    const settle = (): void => {
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const unsubscribe = subscribe(formStore.state.formVersion, () => {
      if (mirroredModifiedAtMillis() >= restoredAtMillis) settle();
    });
    const timer = setTimeout(settle, RESTORE_MIRROR_TIMEOUT_MS);
  });

/**
 * The moment at `cursorMicros`, read off the merged timeline the sidebar draws
 * rather than off `points`: `points` holds the version ladder alone, so a moment at
 * which only a table reported is not in it and looking there returned nothing for
 * exactly the rows this panel added. `undefined` when the timeline no longer holds
 * that moment, which only a clear or a re-listing can cause.
 */
const momentAt = (cursorMicros: number): RecoveryMoment | undefined =>
  formRecoveryStore.state.moments.find((moment) => moment.cursorMicros === cursorMicros);

/**
 * One table's request entry, built from the `tableReads` entry stamped with this
 * very cursor, or `null` when that table has no successful read to take digests
 * from — still out, refused, or dropped by a selection that moved. A read filed
 * against another moment is not a fallback: the digests and the cursor they were
 * measured at travel in one object precisely so a restore cannot pick up one
 * without the other.
 */
const restoreTableAt = (tableId: ArTableId, cursorMicros: number): RestoreRequestTable | null => {
  const read = formRecoveryStore.state.tableReads.find(
    (entry) =>
      entry.tableId === tableId && entry.atCursorMicros === cursorMicros && entry.status === 'read',
  );
  if (read?.result == null) return null;

  return {
    tableId,
    expectedRetainedDigest: read.result.retainedDigest,
    expectedLiveDigest: read.result.liveDigest,
  };
};

/**
 * One table's written rows, counted by what the restore did to each. The whole row
 * diff is not kept — it runs to hundreds of entries and `restoredFrom` outlives the
 * route's other read state — and three counts are what the notice reads back in the
 * confirmation's own words.
 */
const restoredRowCounts = (
  restored: readonly ArTableRowDiffEntry[],
): Record<ArTableRowDiffEntry['action'], number> => ({
  edit: restored.filter((row) => row.action === 'edit').length,
  recreate: restored.filter((row) => row.action === 'recreate').length,
  delete: restored.filter((row) => row.action === 'delete').length,
});

/**
 * Whether the page is still waiting for exactly this restore. Everything compared
 * against was captured before the await — the ids the request names and the token it
 * was sent under — so this is the guard the "never read the bare proxy after an await"
 * rule exists to make possible.
 *
 * `restoring` alone cannot be the token, because it is one flag for every restore: a
 * stale call landing after the operator started a second one would find it raised by
 * that second call and write its own banner, undo entry and `select(null)` over it.
 * `restoreRequestId` is what tells the two apart, on the model of `listRequestId`.
 */
const stillWanted = (request: RestoreFormVersionRequest, requestId: number): boolean =>
  formStore.state.formId === request.formId &&
  formStore.state.versionId === request.formVersionId &&
  formRecoveryStore.state.restoring &&
  formRecoveryStore.state.restoreRequestId === requestId;

/**
 * The commit time the previewed read landed on, or `null` when the store holds no read
 * for the cursor being restored. Guarded on the selection because that read and the
 * digests beside it are what the operator confirmed against: a read filed for another
 * moment says nothing about this one.
 */
const previewedCommittedAtMillis = (cursorMicros: number): number | null =>
  formRecoveryStore.state.selectedCursorMicros === cursorMicros &&
  formRecoveryStore.state.readStatus === 'read'
    ? formRecoveryStore.state.result?.committedAtMillis ?? null
    : null;

/**
 * One `restored` entry as an undo change, or `null` when this adapter cannot
 * correctly revert it — an add, a delete, or any entity reference (see file
 * JSDoc).
 */
const changeFor = (formVersionId: string, entry: FormPitrDiffEntry): UndoChange | null => {
  if (entry.kind !== 'field') return null;
  if (entry.retained === null || entry.live === null) return null;

  return {
    docId: formVersionId,
    path: `fields.${entry.fieldId}.value`,
    prev: entry.live.value,
    next: entry.retained.value,
  };
};

export const useFormRecoveryRestore = (): FormRecoveryRestore => {
  const snap = useSnapshot(formStore.state);
  const formId = snap.formId;
  const formVersionId = snap.versionId;

  /**
   * `read` resolves a dot-path off the live version the store already mirrors;
   * `apply` is the only write path and rethrows on failure so a failed revert
   * never reaches the redo stack (`useFieldUI.tsx:359` is the precedent).
   */
  const adapter = useMemo<UndoTarget>(
    () => ({
      read: (_docId, path) => get(formStore.state.formVersion.data, path),
      apply: async (_docId, writes) => {
        const persist = formStore.getFormVersionPersist();
        if (persist === null) {
          throw new Error('No form version open to write the reverted values into');
        }
        await persist.update(
          Object.fromEntries(writes.map((write) => [write.path, write.value])),
          { debounce: false },
        );
      },
    }),
    [],
  );

  useEffect(() => {
    if (formId === undefined || formVersionId === undefined) return undefined;
    return formUndoStack.registerTarget(docPathFor(formId, formVersionId), adapter);
  }, [formId, formVersionId, adapter]);

  const restore = useCallback(
    async (
      atCursorMicros: number,
      expectedLastModifiedMillis: number | null,
      { skipBlockedRows }: FormRecoveryRestoreOptions,
    ): Promise<RestoreFormVersionResponse> => {
      if (formId === undefined || formVersionId === undefined) {
        throw new Error('No form version open to restore into');
      }

      /**
       * Captured before the await, like the ids. Its absence is a refusal, not a
       * fallback: the only way the moment can be missing is a clear landing between
       * the dialog opening and Confirm, so the state the operator confirmed against
       * is gone and the tables of the moment are gone with it.
       */
      const moment = momentAt(atCursorMicros);
      if (moment === undefined) {
        throw new Error(
          'That restore point is no longer loaded. Close this and select it again.',
        );
      }

      /**
       * A table of the moment with no successful read at this cursor has no digests,
       * so it cannot be named — and a request that simply left it out would restore
       * the form around it. Refusing is the whole-or-nothing the one transaction
       * already promises on the server side.
       */
      const unreadTable = moment.tables.find(
        (table) => restoreTableAt(table.tableId, atCursorMicros) === null,
      );
      if (unreadTable !== undefined) {
        throw new Error(
          `${unreadTable.tableId} was not read at this restore point, so nothing was restored. `
            + 'Close this and select the restore point again.',
        );
      }

      const tables = moment.tables.flatMap(
        (table) => restoreTableAt(table.tableId, atCursorMicros) ?? [],
      );

      /**
       * The version's own commit time, which the server refuses the write against
       * unless the version it reads at the cursor still carries it — a cursor minted
       * inside the exact-time hour and used after that hour has moved past it
       * addresses a whole minute, which may hold a neighbouring version.
       *
       * A moment at which only tables reported still has one, and it has to be sent.
       * The restore writes the version's fields on such a moment too — a table-only
       * moment means the version ladder published no row in that minute, not that the
       * retained version equals live — and nothing else pins that side: a table's
       * `expectedRetainedDigest` covers its rows, `expectedLastModifiedMillis` covers
       * live. So the fallback is the commit time the previewed read landed on, which is
       * the version state the confirmation actually showed. `null` only when no such
       * read is held, and then the cursor decides as it did before.
       */
      const expectedCommittedAtMillis =
        moment.version?.committedAtMillis ?? previewedCommittedAtMillis(atCursorMicros);

      /** The moment the sidebar row named, which is the one the banner has to name back. */
      const momentAtMillis = moment.atMillis;

      const request: RestoreFormVersionRequest = {
        formId,
        formVersionId,
        atCursorMicros,
        expectedLastModifiedMillis,
        expectedCommittedAtMillis,
        tables,
        skipBlockedRows,
      };

      formRecoveryStore.state.restoreRequestId += 1;
      const requestId = formRecoveryStore.state.restoreRequestId;
      formRecoveryStore.state.restoring = true;
      formRecoveryStore.state.restoreError = null;
      try {
        const response = await restoreFormVersionTo(request);
        if (!stillWanted(request, requestId)) return response;

        const changes = response.restored
          .map((entry) => changeFor(formVersionId, entry))
          .filter((change): change is UndoChange => change !== null);

        /**
         * The entry's own guard reads the live values off the store's mirror, so
         * recording before the listener has delivered this write arms an undo that
         * conflicts on the first press and takes the redo stack and the banner with it.
         * A restore with no changes to record waits for nothing: `restoredAtMillis` is
         * not a baseline at all when the transaction wrote no version document.
         */
        if (changes.length > 0) await restoreLanded(response.restoredAtMillis);
        if (!stillWanted(request, requestId)) return response;

        const undoEntryId = formUndoStack.record(docPathFor(formId, formVersionId), changes);

        /**
         * The response's own account of every table, kept for the life of the banner:
         * what was written, what was left behind, and the trash operation a row-level
         * undo would act on. `skipped` is the only record of which rows are at the
         * retained moment and which are still at the present, so a restore that
         * skipped rows can never report itself as a clean one.
         */
        formRecoveryStore.state.restoredFrom = {
          atMillis: momentAtMillis,
          count: response.restored.length,
          tables: response.tables.map((table) => ({
            tableId: table.tableId,
            restored: restoredRowCounts(table.restored),
            skipped: table.skipped,
            trashOperationId: table.trashOperationId,
          })),
          undoEntryId,
        };

        /**
         * The read that put this row on screen no longer describes reality — live
         * now matches retained — so the selection, its result and any preview tint
         * are dropped rather than left showing a diff that just closed. The loaded
         * walk stays: those rows are still restore points.
         */
        formRecoveryStore.select(null);

        return response;
      } catch (error) {
        if (stillWanted(request, requestId)) {
          formRecoveryStore.state.restoreError = readCallableError(error).message;
        }
        throw error;
      } finally {
        /**
         * Only the call that raised the token may lower it. A stale restore landing
         * after the operator started a second one would otherwise retire the live
         * call's own guard, and that restore would commit with no banner, no
         * `restoredFrom` and no undo, leaving the page in preview.
         */
        if (formRecoveryStore.state.restoreRequestId === requestId) {
          formRecoveryStore.state.restoring = false;
        }
      }
    },
    [formId, formVersionId],
  );

  return {
    canRestore: formId !== undefined && formVersionId !== undefined,
    restore,
  };
};
